import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';

export const useAbonnements = () => {
  const { apiCall } = useApi();
  const [data, setData] = useState({ plans: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAbonnements = useCallback(async (filters = {}) => {
    // Vérifier la disponibilité du token et des données utilisateur
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger les abonnements');
      setData({ plans: [], stats: {} });
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
          queryParams.append(key, filters[key]);
        }
      });

      const queryString = queryParams.toString();
      const endpoint = queryString ? `/abonnements?${queryString}` : '/abonnements';
      
      const result = await apiCall(endpoint);
      
      // S'assurer que result a la bonne structure
      const formattedData = {
        plans: result?.plans || [],
        stats: result?.stats || {
          total_plans: 0,
          total_subscriptions: 0,
          active_subscriptions: 0,
          renewal_rate: 0,
          monthly_revenue: 0
        }
      };
      setData(formattedData);
    } catch (error) {
      console.error('Erreur lors de la récupération des abonnements:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchAbonnements();
  }, []);

  return { data, loading, error, refetch: fetchAbonnements };
};

export const useAbonnement = (id) => {
  const { apiCall } = useApi();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAbonnement = useCallback(async () => {
    if (!id) return;
    
    // Vérifier la disponibilité du token et des données utilisateur
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger le plan');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const result = await apiCall(`/abonnements/${id}`);
      setPlan(result);
    } catch (error) {
      console.error('Erreur lors de la récupération du plan:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [id, apiCall]);

  useEffect(() => {
    fetchAbonnement();
  }, [id]);

  return { plan, loading, error, refetch: fetchAbonnement };
};

export const useAbonnementActions = () => {
  const { apiCall } = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createPlan = useCallback(async (planData) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiCall(`/abonnements`, {
        method: 'POST',
        body: JSON.stringify(planData)
      });
      return result;
    } catch (error) {
      console.error('Erreur lors de la création du plan:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  const updatePlan = useCallback(async (id, planData) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiCall(`/abonnements/${id}`, {
        method: 'PUT',
        body: JSON.stringify(planData)
      });
      return result;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du plan:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  const togglePlanStatus = useCallback(async (id, isActive) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiCall(`/abonnements/${id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: isActive })
      });
      return result;
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  const deletePlan = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiCall(`/abonnements/${id}`, {
        method: 'DELETE'
      });
      return result;
    } catch (error) {
      console.error('Erreur lors de la suppression du plan:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  return {
    createPlan,
    updatePlan,
    togglePlanStatus,
    deletePlan,
    loading,
    error
  };
};